import { CardContent, Grid, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { QueryActivityPagePeriod } from '../../../api/schema';
import { CardHeaderFlex } from '../../../component/cardHeaderFlex/CardHeaderFlex';
import LinearProgress from '../../../component/linearProgress/LinearProgress';
import { useIsRemediation } from '../../../hooks/useIsRemediation';
import useSearchParams from '../../../hooks/useSearchParams';
import {
  fetchActivityDetail,
  fetchActivitySummary,
  getActivityDetailFetching,
  getActivityDetailFor,
  getActivitySummaryFetching,
  getActivitySummaryFor,
} from '../../../redux/slices/activity/activitySlice';
import { getLastRefresh } from '../../../redux/slices/app/appSlice';
import { TOTAL_SERIES, getUtilizationSeries, itemUtilizationHistory, itemUtilizationHistoryFetching } from '../../../redux/slices/tools/toolsSlice';
import { getMarketsList } from '../../../redux/slices/verticalMarkets/verticalMarketsSlice';
import { useDispatch } from '../../../redux/store';
import { CardStyled } from '../../../styled/components';
import { viewerTimeZone } from '../../dashboard/kpiWindow';
import { TREND_PERIODS } from '../../dashboard/periodChart';
import ActivityBarChart, { ActivityBar } from './ActivityBarChart';
import ActivityControls from './ActivityControls';
import ActivityPeriodTable from './ActivityPeriodTable';
import ActivitySnapshot from './ActivitySnapshot';
import BoomerangTables from './BoomerangTables';
import HourlyMovementChart, { HourlyView } from './HourlyMovementChart';
import SetupsList from './SetupsList';
import UtilizationMixCard from './UtilizationMixCard';
import { BOOMERANG_SERIES, EQUIPMENT_SERIES, JOB_SITE_SERIES } from './activityColors';
import { ActivityGrain, detailLabel, detailPath, metricsOf, parseGrain, parseMode, parsePeriod, periodLabel, windowCaption } from './activityWindow';

// One level down from a period is its parts: a month or a week is made of
// days, a day is made of hours. A day therefore has no period list -- its
// hour-by-hour chart is the list.
const FINER: Record<ActivityGrain, ActivityGrain | undefined> = { month: 'day', week: 'day', day: undefined };

const CHART_HEIGHT = 190;

/**
 * One period opened up. Deliberately the same page as the level above it: the
 * same snapshot, the same four charts, the same period list -- just a level
 * finer -- plus what only a single period can show: the hour-by-hour movement,
 * the job sites set up for delivery, and the equipment behind the boomerangs.
 *
 * Its bounds come from the row that opened it, so a clipped period drills into
 * exactly what that row showed rather than into a whole calendar week.
 */
export const ToolsActivityDetail = (): ReactElement => {
  const dispatch = useDispatch();
  const { grain: grainParam, start } = useParams();
  const [params] = useSearchParams();
  const grain = parseGrain(grainParam);
  const finer = FINER[grain];
  const tz = useMemo(() => viewerTimeZone(), []);
  const lastRefresh = useSelector(getLastRefresh);
  const navigate = useNavigate();
  const [view, setView] = useState<HourlyView>('day');
  const [showTrend, setShowTrend] = useState(true);

  const isRemediation = useIsRemediation();
  const markets = useSelector(getMarketsList({}));

  // A hand-typed or shortened link still works: fall back to the whole
  // calendar day, week or month that `start` names.
  const { from, to } = useMemo(() => {
    if (params.from && params.to) return { from: params.from, to: params.to };
    const at = DateTime.fromISO(start || '', { zone: tz }).startOf(grain);
    if (!at.isValid) return { from: '', to: '' };
    const unit = grain === 'day' ? { days: 1 } : grain === 'week' ? { weeks: 1 } : { months: 1 };
    return { from: at.toUTC().toISO() || '', to: at.plus(unit).toUTC().toISO() || '' };
  }, [params.from, params.to, start, grain, tz]);

  const detail = useSelector(getActivityDetailFor(from, to));
  const detailFetching = useSelector(getActivityDetailFetching);
  // The same summary the level above uses, measured over this window at the
  // next grain down, so this page can draw the identical four charts.
  const summary = useSelector(getActivitySummaryFor(from, to));
  const summaryFetching = useSelector(getActivitySummaryFetching);
  const utilization = useSelector(getUtilizationSeries);
  const utilizationFetching = useSelector(itemUtilizationHistoryFetching);

  useEffect(() => {
    if (from && to) dispatch(fetchActivityDetail({ from, to, tz }));
  }, [dispatch, from, to, tz, lastRefresh]);

  useEffect(() => {
    if (from && to && finer) dispatch(fetchActivitySummary({ from, to, grain: finer, tz }));
  }, [dispatch, from, to, finer, tz, lastRefresh]);

  useEffect(() => {
    dispatch(itemUtilizationHistory());
  }, [dispatch, lastRefresh]);

  const periods = useMemo(() => summary?.periods || [], [summary]);
  const lead = useMemo(() => summary?.lead || [], [summary]);

  const bars = useCallback(
    (rows: QueryActivityPagePeriod[], pick: 'movement' | 'jobs' | 'boomerangs'): ActivityBar[] =>
      rows.map((row) => {
        const m = metricsOf(row);
        const label = periodLabel(row, finer || 'day', tz);
        const values =
          pick === 'movement' ? [m.equipment_out, m.equipment_in] : pick === 'jobs' ? [m.jobs_opened, m.jobs_closed] : [m.boomerang_real, m.boomerang_artifact];
        return {
          key: row.date || '',
          label: label.replace(/^Week of /, '').replace(/ \(from .*\)$/, ''),
          title: label,
          values,
          notes:
            pick === 'movement'
              ? [
                  { name: 'Jobs opened', value: String(m.jobs_opened) },
                  { name: 'Jobs closed', value: String(m.jobs_closed) },
                  { name: 'Returns / Cancellations', value: `${m.boomerang_real} / ${m.boomerang_artifact}` },
                ]
              : undefined,
        };
      }),
    [finer, tz],
  );

  const movement = useMemo(() => bars(periods, 'movement'), [bars, periods]);
  const movementLead = useMemo(() => bars(lead, 'movement'), [bars, lead]);
  const jobs = useMemo(() => bars(periods, 'jobs'), [bars, periods]);
  const jobsLead = useMemo(() => bars(lead, 'jobs'), [bars, lead]);
  const boomerangs = useMemo(() => bars(periods, 'boomerangs'), [bars, periods]);

  // The average is seeded from the periods before the window, so the line
  // starts on the first one drawn rather than the third.
  const trailingAverage = (leadBars: ActivityBar[], visible: ActivityBar[], seriesCount: number) => {
    const seeded = [...leadBars.slice(-(TREND_PERIODS - 1)), ...visible];
    const offset = seeded.length - visible.length;
    return Array.from({ length: seriesCount }, (_, series) =>
      visible.map((_bar, index) => {
        const end = index + offset;
        if (end < TREND_PERIODS - 1) return undefined;
        const slice = seeded.slice(end - TREND_PERIODS + 1, end + 1);
        return slice.reduce((sum, bar) => sum + bar.values[series], 0) / slice.length;
      }),
    );
  };

  if (markets.result && !isRemediation) {
    return <Navigate to="/activity/dashboard/summary" replace />;
  }

  const period = parsePeriod(params.period);
  const mode = parseMode(params.mode);
  // Up is wherever this page was opened from: the enclosing period when the
  // link carried one, otherwise the window list itself.
  const upTo = params.up || `/activity/dashboard/activity?period=${period}&mode=${mode}`;
  const heading = detailLabel(from, to, grain, tz);
  // A month is 30 bars; a label under each one is unreadable.
  const labelEvery = periods.length > 20 ? 4 : periods.length > 10 ? 2 : 1;
  const days = detail?.window?.days;
  const fetching = detailFetching || summaryFetching;

  // Opening a sub-period keeps a trail: this page becomes the one above it.
  const here = `${window.location.pathname}${window.location.search}`;
  const openPeriod = (index: number) => {
    const row = periods[index];
    if (!row || !finer) return;
    navigate(`${detailPath(row, finer, period, mode)}&up=${encodeURIComponent(here)}`);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const openDay = (date: string) => {
    const dayStart = DateTime.fromISO(date, { zone: tz }).startOf('day');
    if (!dayStart.isValid) return;
    const search = new URLSearchParams({
      from: dayStart.toUTC().toISO() || '',
      to: dayStart.plus({ days: 1 }).toUTC().toISO() || '',
      period,
      mode,
      up: here,
    });
    navigate(`/activity/dashboard/activity/day/${date}?${search.toString()}`);
  };

  return (
    <>
      {fetching && <LinearProgress />}

      <Typography variant="h3" sx={{ mb: 0.5 }}>
        {heading}
      </Typography>

      <ActivityControls
        period={period}
        mode={mode}
        caption={windowCaption(from, to, days, tz)}
        upTo={upTo}
        showTrend={showTrend}
        onPeriod={(value) => navigate(`/activity/dashboard/activity?period=${value}&mode=${mode}`)}
        onMode={(value) => navigate(`/activity/dashboard/activity?period=${period}&mode=${value}`)}
        onShowTrend={setShowTrend}
      />

      <ActivitySnapshot metrics={detail?.metrics} days={days} fetching={fetching} />

      <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mb: 4 }} alignItems="stretch">
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <CardStyled sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeaderFlex
              title="Equipment"
              legend={
                grain === 'day' ? undefined : (
                  <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, value) => value && setView(value as HourlyView)}>
                    <ToggleButton value="day" sx={{ textTransform: 'none', px: 1.5 }}>
                      By day
                    </ToggleButton>
                    <ToggleButton value="clock" sx={{ textTransform: 'none', px: 1.5 }}>
                      By hour
                    </ToggleButton>
                  </ToggleButtonGroup>
                )
              }
            />
            <CardContent sx={{ pt: 0, flexGrow: 1, '&:last-child': { pb: 2 } }}>
              {/* A day is drawn from its hours; anything longer from its own
                  sub-periods, which carry the trend and the drill-down. */}
              {grain === 'day' || view === 'clock' ? (
                <HourlyMovementChart hourly={detail?.hourly || []} grain={grain} view={view} tz={tz} fetching={fetching} height={CHART_HEIGHT} />
              ) : (
                <ActivityBarChart
                  bars={movement}
                  series={EQUIPMENT_SERIES}
                  trend={showTrend ? trailingAverage(movementLead, movement, 2) : undefined}
                  trendLabel={`${TREND_PERIODS}-${finer} avg`}
                  fetching={fetching}
                  height={CHART_HEIGHT}
                  labelEvery={labelEvery}
                  formatValue={(value) => Math.round(value).toLocaleString()}
                  onSelect={openPeriod}
                />
              )}
            </CardContent>
          </CardStyled>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <UtilizationMixCard points={utilization.byCategory[TOTAL_SERIES] || []} from={from} to={to} fetching={utilizationFetching} />
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <CardStyled sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeaderFlex title="Job Sites" />
            <CardContent sx={{ pt: 0, flexGrow: 1, '&:last-child': { pb: 2 } }}>
              {finer ? (
                <ActivityBarChart
                  bars={jobs}
                  series={JOB_SITE_SERIES}
                  trend={showTrend ? trailingAverage(jobsLead, jobs, 2) : undefined}
                  trendLabel={`${TREND_PERIODS}-${finer} avg`}
                  fetching={fetching}
                  height={CHART_HEIGHT}
                  labelEvery={labelEvery}
                  emptyText="No job sites opened or closed in this period"
                  onSelect={openPeriod}
                />
              ) : (
                // A day has no finer job-site grain: the two numbers for the
                // day are already in the snapshot above.
                <SetupsList setups={detail?.setups || []} fetching={fetching} />
              )}
            </CardContent>
          </CardStyled>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <CardStyled sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeaderFlex title="Boomerangs" />
            <CardContent sx={{ pt: 0, flexGrow: 1, '&:last-child': { pb: 2 } }}>
              {finer && view === 'day' ? (
                <ActivityBarChart
                  bars={boomerangs}
                  series={BOOMERANG_SERIES}
                  fetching={fetching}
                  height={CHART_HEIGHT}
                  labelEvery={labelEvery}
                  emptyText="No round trips in this period"
                  onSelect={openPeriod}
                />
              ) : (
                <HourlyMovementChart hourly={detail?.hourly || []} grain={grain} view={view} tz={tz} fetching={fetching} boomerangs height={CHART_HEIGHT} />
              )}
            </CardContent>
          </CardStyled>
        </Grid>
      </Grid>

      {finer && (
        <ActivityPeriodTable
          periods={periods}
          grain={finer}
          period={period}
          mode={mode}
          tz={tz}
          fetching={fetching}
          linkSuffix={`&up=${encodeURIComponent(here)}`}
        />
      )}

      {!finer && (
        <Stack spacing={4} sx={{ mt: 4 }}>
          <SetupsList setups={detail?.setups || []} fetching={fetching} />
        </Stack>
      )}

      <Stack spacing={0} sx={{ mt: 4 }}>
        <BoomerangTables real={detail?.boomerangs?.real || []} artifact={detail?.boomerangs?.artifact || []} tz={tz} fetching={fetching} />
      </Stack>
    </>
  );
};

export default ToolsActivityDetail;
